import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, generatePath } from 'react-router-dom';

import { withLoader } from 'src/core/components';
import { PLATE_NAME } from 'src/constants/forms';
import {
  selectLoaderData,
  selectDetailsData,
} from 'src/core/redux/selectors';
import { Plate, DnaOligo } from 'src/models';
import { routes as routeConstants } from 'src/constants';
import { selectFileData } from 'src/components/OligoDNA/components/UploadPlateTemplate/selectors';
import {
  refreshPlates as refreshPlatesAction
} from 'src/components/OligoDNA/components/UploadPlateTemplate/actions';
import { User } from 'src/core/models';

import {
  fetchPlateDetails as fetchPlateDetailsAction,
  updatePlate as updatePlateAction
} from './actions';
import EditPlateComponent from './EditPlateComponent';

const getDefaultFormState = (plate = {}) => ({
  [PLATE_NAME]: {
    value: plate[PLATE_NAME] || '',
    isValid: !!plate[PLATE_NAME],
  },
});

const EditPlateContainer = ({
  fetchPlateDetails,
  fetchPayload,
  plateDetails,
  updatePlate,
  history,
  match,
  showLoader,
  hideLoader,
  refreshPlates,
  plateSize,
  isLoading,
  userDetails,
}) => {
  const [formState, setFormState] = React.useState(getDefaultFormState());
  const [enableActionButtons, setEnableActionButtons] = React.useState(false);
  const dnaType = DnaOligo.getDNAType(userDetails);
  const onChangeField = (value, fieldName, isValid) => {
    setFormState((prevState) => ({
      ...prevState,
      [fieldName]: {
        ...prevState[fieldName],
        value: value || '',
        isValid,
      }
    }));

    if (plateDetails[fieldName] !== value) {
      setEnableActionButtons(true);
    } else {
      setEnableActionButtons(false);
    }
  };

  const goBack = () => {
    setEnableActionButtons(false);
    let newPath = routeConstants.OLIGODNA_PAGE;

    if (dnaType) {
      newPath = `${routeConstants.OLIGODNA_PAGE}?${dnaType}`;
    }

    history.replace(newPath);
  };

  const onSubmit = async () => {
    showLoader();

    const updateIsValid = await updatePlate({
      ...Plate.processApiData(formState),
      oldPlateName: match?.params?.plateName,
    }, setEnableActionButtons);

    if (updateIsValid) {
      // change route to new path edit-late/<new-plate-name>/
      const newPath = generatePath(
        routeConstants.EDIT_PLATE_PAGE,
        { plateName: formState[PLATE_NAME]?.value }
      );

      history.replace(newPath);
    }

    hideLoader();
  };

  React.useEffect(() => {
    onChangeField(plateDetails.plateName, PLATE_NAME, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plateDetails]);

  React.useEffect(() => {
    if (!plateSize) {
      // todo: if this is needed de-comment to refresh plate data (otherwise error message shown)
      // for this to work compare redux states with upload file and see missing summary items
      // they should be returned by api before integrating this
      // refreshPlates(true);
    }
  }, [plateSize, refreshPlates]);

  React.useEffect(() => {
    fetchPlateDetails(fetchPayload);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlateDetails]);

  const onWellClick = (well) => {
    const wellPath = `${generatePath(
      routeConstants.EDIT_SEQUENCE_PAGE,
      { plateName: fetchPayload.plateName }
    )}?row=${well.row}&column=${well.column}`;

    history.push(wellPath);
  };

  if (isLoading) {
    return null;
  }

  return (
    <EditPlateComponent
      {...{
        onChangeField,
        formState,
        onCancel: goBack,
        onSubmit,
        canSubmit: Plate.isDataValid(formState),
        onBackClick: goBack,
        wells: plateDetails.plateWells,
        onWellClick,
        plateSize,
        enableActionButtons
      }}
    />
  );
};

EditPlateContainer.propTypes = {
  fetchPlateDetails: PropTypes.func.isRequired,
  fetchPayload: PropTypes.object.isRequired,
  plateDetails: PropTypes.object,
  updatePlate: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  showLoader: PropTypes.func.isRequired,
  hideLoader: PropTypes.func.isRequired,
  plateSize: PropTypes.number,
  refreshPlates: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  userDetails: PropTypes.object,
};

const makeMapStateToProps = () => {
  const plateDetailsSelector = selectDetailsData(Plate);

  const mapStateToProps = (state, ownProps) => {
    const fileData = selectFileData(state);
    const userDetails = selectDetailsData(User)(state);

    return {
      plateSize: fileData?.data?.plateSize,
      fetchPayload: ownProps.match?.params, // is an object of shape { plateName: <value here> }
      plateDetails: plateDetailsSelector(state),
      isLoading: selectLoaderData(state),
      userDetails,
    };
  };

  return mapStateToProps;
};

const mapDispatchToProps = ({
  fetchPlateDetails: fetchPlateDetailsAction,
  updatePlate: updatePlateAction,
  refreshPlates: refreshPlatesAction,
});

export default compose(
  withRouter,
  withLoader,
  connect(makeMapStateToProps, mapDispatchToProps)
)(EditPlateContainer);
